<template>
    <CommonPages id="cities" :class="['cities-page']" :titlePrefix="pageMetaTitle"
        :metaDescription="pageMetaDescription">
        <section class="cities-page__hero" :style="heroBackgroundStyle">
            <div class="cities-page__hero-overlay"></div>
            <div class="cities-page__hero-top"></div>
            <div class="cities-page__hero-bottom"></div>
            <div class="cities-page__hero-content">
                <h1 class="cities-page__hero-title">{{ heroTitle }}</h1>
                <p class="cities-page__hero-subtitle">{{ heroSubtitle }}</p>
            </div>
        </section>

        <div v-if="citiesAds.heroBottom" class="cities-page__section-ad cities-page__section-ad--top">
            <AdComponent class="ad-width-100" :ads="citiesAds.heroBottom" :showDebug="showDebug" :forceDebug="true"
                debugLabel="cities_ad_1" debugPosition="Cities Hero 下方（首个广告）" />
        </div>

        <section class="cities-page__main">
            <aside class="cities-page__sidebar">
                <ExploreFilter :standardsTopAd="deviceType == 'mobile' ? citiesAds.mobileStandardsTop : null"
                    :showAdDebug="showDebug" />
            </aside>
            <div class="cities-page__content">
                <ExploreGrid :inlineAds="citiesGridInlineAds" :showAdDebug="showDebug" />
            </div>
        </section>

        <div v-if="deviceType != 'mobile' && citiesAds.pageBottom" class="cities-page__ad">
            <AdComponent ref="ads-cities" class="ad-width-100" :ads="citiesAds.pageBottom" :showDebug="showDebug"
                :forceDebug="true" debugLabel="cities_ad_3" debugPosition="Cities 页面底部（仅PC）" />
        </div>
    </CommonPages>
</template>

<script>
import { mapGetters } from 'vuex';
import CommonPages from '@/pages/components/page_common.vue';
import AdComponent from '@/pages/components/AdComponent.vue';
import ExploreFilter from '@/components/explore/ExploreFilter.vue';
import ExploreGrid from '@/components/explore/ExploreGrid.vue';
import adMixin from '@/mixin/adMixin.js';
import adTrackingMixin from '@/mixin/adTrackingMixin.js';
import { getContentTextMap, getText } from '@/utils/contentText';
import { getRenderableAd } from '@/utils/adConfig';
const CITIES_HERO_IMAGE =
    'https://images.unsplash.com/photo-1494522358652-f30e61a60313?auto=format&fit=crop&w=2400&q=80';

export default {
    name: 'CitiesPage',
    components: {
        CommonPages,
        AdComponent,
        ExploreFilter,
        ExploreGrid
    },
    mixins: [adMixin, adTrackingMixin],
    computed: {
        ...mapGetters({
            domainConfig: 'domainConfig',
            adsensConfig: 'adsensConfig',
            showDebug: 'showDebug',
            deviceType: 'deviceType'
        }),
        citiesAds() {
            const cfg = this.adsensConfig || {};
            return {
                mobileStandardsTop: getRenderableAd(cfg.filter_1),
                heroBottom: this.deviceType == 'mobile' ? null : getRenderableAd(cfg.filter_1),
                gridRow: getRenderableAd(cfg.filter_2),
                mobileFourthRow: getRenderableAd(cfg.filter_3),
                pageBottom: getRenderableAd(cfg.filter_3),
            };
        },
        citiesGridInlineAds() {
            return {
                row1: this.deviceType == 'mobile' ? this.citiesAds.gridRow : null,
                row2: this.deviceType == 'mobile' ? null : this.citiesAds.gridRow,
                row4: this.deviceType == 'mobile' ? this.citiesAds.mobileFourthRow : null,
            };
        },
        heroBackgroundStyle() {
            return {
                backgroundImage: `url(${CITIES_HERO_IMAGE})`
            };
        },
        contentText() {
            return getContentTextMap(this.domainConfig);
        },
        pageMetaTitle() {
            return getText(this.contentText, 'CITIES_META_TITLE', this.heroTitle);
        },
        heroTitle() {
            return 'Search Your Next Home';
        },
        heroSubtitle() {
            return '50 Livable Cities Around the World';
        },
        pageMetaDescription() {
            return getText(this.contentText, 'CITIES_META_DESCRIPTION', '50 Livable Cities Around the World');
        }
    }
};
</script>

<style lang="scss" scoped>
@import '~@/css/variables.scss';

.cities-page {
    background: $color-bg-cream;
    /* rgb(255,247,225) in figma */

    &__hero {
        position: relative;
        height: rem(400);
        background-size: cover;
        background-position: center;
        overflow: hidden;
    }

    &__hero-overlay,
    &__hero-bottom,
    &__hero-top {
        position: absolute;
        left: 0;
        right: 0;
        pointer-events: none;
    }

    &__hero-overlay {
        top: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.3);
    }

    &__hero-top {
        top: 0;
        height: rem(120);
        background: linear-gradient(180deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0.4) 49.88%, rgba(0, 0, 0, 0) 100%);
    }

    &__hero-bottom {
        bottom: 0;
        height: rem(220);
        background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.4) 49.88%, rgba(0, 0, 0, 0.6) 100%);
    }

    &__hero-content {
        position: relative;
        z-index: 2;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: rem(24);
        padding-top: rem(88);
        text-align: center;
        text-shadow: 0 rem(2) rem(4) rgba(0, 0, 0, 0.16);
        color: #fff;
    }

    &__hero-ad {
        width: 100%;
        padding: 0 rem(20);
        box-sizing: border-box;
    }

    &__hero-title {
        font-family: $font-family-serif;
        font-size: rem(52);
        font-weight: 700;
        line-height: rem(60);
        margin: 0;
    }

    &__hero-subtitle {
        font-family: $font-family-serif;
        font-size: rem(28);
        font-weight: 500;
        line-height: rem(36);
        margin: 0;
    }

    &__main {
        padding: rem(40) rem(20);
        display: flex;
        flex-direction: column;
        gap: rem(24);
        max-width: rem(1080);
        margin: 0 auto;
    }

    &__sidebar {
        width: 100%;
        margin-bottom: rem(24);
    }

    &__content {
        flex: 1;
        min-width: 0;
    }

    &__ad {
        background: transparent;
        padding: rem(16) rem(20) 0;
        box-sizing: border-box;
    }

    &__section-ad {
        padding: rem(16) rem(20) 0;
        box-sizing: border-box;
    }

    &__header-ad {
        padding: rem(60) rem(20) 0;
        box-sizing: border-box;
        background: $color-bg-cream;
    }

    &__sidebar-ad {
        margin-top: rem(16);
    }

    &__content-ad {
        margin-bottom: rem(20);
    }
}

.detect_deviece_pc,
.detect_deviece_desktop {
    .cities-page {
        &__main {
            padding: rem(60) rem(180);
            flex-direction: row;
            gap: rem(24);
            max-width: none;
            margin: 0;
        }

        &__sidebar {
            width: rem(200);
            flex-shrink: 0;
            margin-bottom: 0;
            padding: 0;
            position: sticky;
            top: rem(120);
            height: fit-content;
        }

        &__section-ad {
            padding: rem(20) rem(180) 0;
        }

        &__header-ad {
            padding: rem(60) rem(180) 0;
        }

        &__hero-ad {
            padding: 0 rem(120);
        }

        &__ad {
            padding: rem(20) rem(180) 0;
        }
    }
}

.detect_deviece_mobile {
    .cities-page {
        &__hero {
            height: rem(260);
        }

        &__hero-top {
            height: rem(90);
        }

        &__hero-bottom {
            height: rem(120);
        }

        &__hero-content {
            gap: rem(10);
            padding: rem(72) rem(20) 0;
        }

        &__hero-title {
            font-size: rem(32);
            line-height: rem(36);
        }

        &__hero-subtitle {
            font-size: rem(18);
            line-height: rem(24);
        }

        &__sidebar {
            margin-bottom: rem(20);
        }

        &__content-ad {
            margin-bottom: rem(16);
        }

        &__ad {
            padding: rem(12) rem(16) 0;
        }

        &__header-ad {
            padding: rem(60) rem(16) 0;
        }
    }
}
</style>
